import { useState } from 'react';

interface IDEIconProps {
  name: string;
  icon: React.ReactNode;
  hoverColor?: string;
}

export function IDEIcon({ name, icon }: Readonly<IDEIconProps>) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="relative group">
      <button
        type="button"
        className="cursor-pointer transition-all duration-300 bg-transparent border-none p-0"
        aria-label={name}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        style={{
          filter: isHovered ? 'none' : 'grayscale(100%)',
          opacity: isHovered ? 1 : 0.6,
        }}
      >
        {icon}
      </button>

      {/* Tooltip */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
        {name}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 -mb-px">
          <div className="border-4 border-transparent border-b-gray-900"></div>
        </div>
      </div>
    </div>
  );
}

// VS Code Icon
export function VSCodeIcon() {
  return (
    <svg viewBox="0 0 24 20" fill="none" className="h-4 w-4" aria-hidden="true">
      <path
        d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L3.899 12 .326 15.26a1 1 0 0 0 .001 1.479L1.65 17.94a.999.999 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 20.06V3.939a1.5 1.5 0 0 0-.85-1.352zm-5.146 14.861L10.826 12l7.178-5.448v10.896z"
        fill="#007ACC"
      />
    </svg>
  );
}

// Cursor Icon
export function CursorIcon() {
  return (
    <svg
      width="14"
      height="16"
      viewBox="0 0 14 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.7209 3.78719L7.3314 0.0890437C7.12613 -0.0296812 6.87293 -0.0296812 6.66795 0.0890437L0.278782 3.78719C0.106408 3.88688 0 4.07174 0 4.27148V11.7285C0 11.9286 0.106408 12.1131 0.278782 12.2128L6.66831 15.911C6.87352 16.0297 7.12678 16.0297 7.33169 15.911L13.7212 12.2128C13.8936 12.1131 14 11.9283 14 11.7285V4.27148C14 4.07144 13.8936 3.88688 13.7212 3.78719H13.7209ZM13.3195 4.57057L7.15142 15.2804C7.10974 15.3526 6.99956 15.3232 6.99956 15.2396V8.22693C6.99956 8.08675 6.92469 7.95727 6.80378 7.88701L0.745501 4.38069C0.673522 4.33891 0.702882 4.2284 0.786239 4.2284H13.1228C13.2981 4.2284 13.4077 4.41863 13.3199 4.57092L13.3195 4.57057Z"
        fill="#EDECEC"
        opacity="0.6"
      />
    </svg>
  );
}

// Windsurf Icon
export function WindsurfIcon() {
  return (
    <svg
      width="21"
      height="12"
      viewBox="0 0 21 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.565 0.00324292H20.3672C19.3263 0.00162216 18.4815 0.844749 18.4815 1.88569V6.09564C18.4815 6.93634 17.7868 7.61733 16.9598 7.61733C16.4685 7.61733 15.9781 7.37006 15.6871 6.95499L11.3871 0.813943C11.0304 0.304013 10.4499 0 9.82166 0C8.84148 0 7.95945 0.833399 7.95945 1.86218V6.09646C7.95945 6.93716 7.27035 7.61814 6.43777 7.61814C5.94486 7.61814 5.4552 7.37088 5.16416 6.9558L0.352654 0.0843131C0.244023 -0.0705299 0 0.00567481 0 0.195379V3.86703C0 4.05268 0.0567484 4.23267 0.162953 4.38507L4.89744 11.1471C5.17713 11.5468 5.58977 11.8435 6.06566 11.9513C7.25657 12.2221 8.35264 11.3052 8.35264 10.137V5.90513C8.35264 5.06444 9.03365 4.38345 9.87428 4.38345H9.87679C10.3835 4.38345 10.8585 4.63071 11.1496 5.04579L15.4495 11.186C15.807 11.6967 16.3575 12 17.0141 12C18.0162 12 18.8747 11.1657 18.8747 10.1378V5.90432C18.8747 5.06363 19.5557 4.38264 20.3964 4.38264H20.5642C20.6696 4.38264 20.7547 4.29752 20.7547 4.19213V0.192947C20.7547 0.0875561 20.6696 0.00243188 20.5642 0.00243188L20.565 0.00324292Z"
        fill="white"
        opacity="0.6"
      />
    </svg>
  );
}

// Warp Icon
export function WarpIcon() {
  return (
    <svg
      width="17"
      height="14"
      viewBox="0 0 17 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.36 0H14.5258C15.5239 0 16.333 0.836128 16.333 1.86754V9.11792C16.333 10.1494 15.5239 10.9855 14.5258 10.9855H5.70231L8.36 0Z"
        fill="white"
        opacity="0.6"
      />
      <path
        d="M6.75878 2.08114H1.79182C0.802225 2.08114 0 2.91726 0 3.94866V11.1991C0 12.2305 0.802225 13.0666 1.79182 13.0666H7.90507L8.15022 12.0446H4.3689L6.75878 2.08114Z"
        fill="white"
        opacity="0.4"
      />
    </svg>
  );
}

// Claude Icon
export function ClaudeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.13933 10.6367L6.286 8.872L6.33933 8.71867L6.286 8.63333H6.13333L5.60667 8.60133L3.808 8.55267L2.24867 8.488L0.738 8.40667L0.357333 8.326L0 7.856L0.0366667 7.62133L0.356667 7.40733L0.814 7.44733L1.82733 7.516L3.346 7.62133L4.44733 7.686L6.08 7.856H6.33933L6.376 7.75133L6.28667 7.686L6.218 7.62133L4.646 6.55733L2.94467 5.432L2.054 4.784L1.57133 4.45667L1.32867 4.14867L1.22333 3.47667L1.66067 2.99533L2.248 3.03533L2.398 3.076L2.99333 3.53333L4.26533 4.51733L5.926 5.73933L6.16933 5.942L6.266 5.87333L6.27867 5.82467L6.16933 5.642L5.266 4.01133L4.302 2.35133L3.87267 1.66333L3.75933 1.25067C3.71615 1.09217 3.69286 0.928916 3.69 0.764667L4.18867 0.0893333L4.464 0L5.128 0.0893333L5.408 0.332L5.82133 1.27467L6.48933 2.76067L7.526 4.78067L7.83 5.37933L7.992 5.934L8.05267 6.104H8.158V6.00667L8.24333 4.86933L8.40133 3.47267L8.55467 1.676L8.608 1.16933L8.85867 0.562667L9.35667 0.234667L9.746 0.421333L10.066 0.878L10.0213 1.174L9.83067 2.408L9.458 4.34333L9.21533 5.638H9.35667L9.51867 5.47667L10.1753 4.606L11.2767 3.23L11.7633 2.68333L12.33 2.08067L12.6947 1.79333H13.3833L13.89 2.546L13.6633 3.32333L12.954 4.22133L12.3667 4.98267L11.524 6.116L10.9973 7.02267L11.046 7.096L11.1713 7.08267L13.0753 6.67867L14.104 6.492L15.3313 6.282L15.8867 6.54067L15.9473 6.804L15.7287 7.342L14.416 7.666L12.8767 7.974L10.584 8.516L10.556 8.536L10.5887 8.57667L11.6213 8.674L12.0627 8.698H13.144L15.1573 8.848L15.684 9.196L16 9.62133L15.9473 9.94467L15.1373 10.358L14.044 10.0987L11.4913 9.492L10.6167 9.27267H10.4953V9.346L11.224 10.058L12.5613 11.2647L14.234 12.818L14.3187 13.2033L14.104 13.5067L13.8773 13.474L12.4073 12.3693L11.84 11.8713L10.556 10.7913H10.4707V10.9047L10.7667 11.3373L12.33 13.6847L12.4113 14.4047L12.298 14.64L11.8927 14.782L11.4473 14.7007L10.5313 13.4173L9.588 11.9727L8.826 10.6773L8.73267 10.7307L8.28333 15.5667L8.07267 15.8133L7.58667 16L7.182 15.6927L6.96733 15.1947L7.182 14.2107L7.44133 12.928L7.65133 11.908L7.842 10.6413L7.95533 10.22L7.94733 10.192L7.854 10.204L6.898 11.5153L5.44467 13.4787L4.294 14.7087L4.018 14.818L3.54 14.5713L3.58467 14.13L3.852 13.7373L5.444 11.7133L6.404 10.4587L7.024 9.73467L7.02 9.62933H6.98333L2.75467 12.3733L2.00133 12.4707L1.67667 12.1667L1.71733 11.6693L1.87133 11.5073L3.14333 10.6327L3.13933 10.6367Z"
        fill="#C6613F"
      />
    </svg>
  );
}

// Cline Icon
export function ClineIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="18" height="18" rx="2" fill="#00b8db" />
      <path
        d="M8 9l3 3-3 3M12 15h4"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
